import { useEffect, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { JobTreeNode } from './JobTreeNode';
import {
  aggregateRootCounts,
  partitionRootsByActivity,
  useJobsTree,
} from './useJobsTree';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Tab = 'active' | 'history';

export function JobsDrawer({ open, onClose }: Props) {
  const tree = useJobsTree();
  const { active, history } = partitionRootsByActivity(tree);
  const counts = aggregateRootCounts(tree);
  const [tab, setTab] = useState<Tab>('active');

  useEffect(() => {
    if (!open) return;
    setTab(active.length > 0 ? 'active' : 'history');
  }, [open]);

  const summaryParts: Array<{ label: string; className?: string }> = [];
  if (counts.running) summaryParts.push({ label: `${counts.running} running`, className: 'text-amber-300' });
  if (counts.pending) summaryParts.push({ label: `${counts.pending} pending` });
  if (counts.failed) summaryParts.push({ label: `${counts.failed} failed`, className: 'text-destructive' });
  if (counts.interrupted)
    summaryParts.push({ label: `${counts.interrupted} stuck`, className: 'text-orange-300' });
  if (counts.succeeded) summaryParts.push({ label: `${counts.succeeded} done` });
  if (counts.canceled) summaryParts.push({ label: `${counts.canceled} canceled` });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const visible = tab === 'active' ? active : history;

  return (
    <>
      <div
        aria-hidden
        className={cn(
          'fixed inset-0 z-30 bg-black/40 transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
      />
      <aside
        aria-label="Jobs"
        className={cn(
          'fixed right-0 top-0 z-40 flex h-full w-full max-w-md flex-col border-l border-border bg-background shadow-2xl transition-transform',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="border-b border-border px-4 py-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">Jobs</h2>
            <button
              type="button"
              aria-label="Close jobs"
              onClick={onClose}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              ×
            </button>
          </div>
          {tree.length > 0 ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
              {summaryParts.length === 0 ? (
                <span>No jobs</span>
              ) : (
                summaryParts.map((p, i) => (
                  <span key={p.label} className={p.className}>
                    {i > 0 ? <span className="text-muted-foreground/60">· </span> : null}
                    {p.label}
                  </span>
                ))
              )}
            </div>
          ) : null}
        </header>
        <div role="tablist" aria-label="Jobs view" className="flex border-b border-border">
          <TabButton
            label="Active"
            count={active.length}
            selected={tab === 'active'}
            onClick={() => setTab('active')}
          />
          <TabButton
            label="History"
            count={history.length}
            selected={tab === 'history'}
            onClick={() => setTab('history')}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {visible.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {tab === 'active'
                ? 'Nothing running. Kick off a brand research or image generation to see it here.'
                : 'No finished jobs yet.'}
            </p>
          ) : (
            <div className="space-y-1">
              {visible.map((n) => (
                <JobTreeNode key={n.job.id} node={n} />
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

function TabButton({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={cn(
        'flex-1 px-3 py-2 text-xs font-medium uppercase tracking-wider transition-colors',
        selected
          ? 'border-b-2 border-foreground text-foreground'
          : 'border-b-2 border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <span>{label}</span>
      <span className="ml-1.5 text-muted-foreground/60">{count}</span>
    </button>
  );
}
